import type { ReactNode } from 'react';

import { SOFT_TONE_CLASS } from '../styles/tone.js';

/**
 * Tones a status chip may carry. These are presentation roles, not a status
 * vocabulary: a caller names the state in the chip's own text (CLAUDE.md §2 —
 * nothing here knows what any particular status means).
 */
export type StatusChipTone = 'active' | 'info' | 'warning' | 'neutral';

export interface StatusChipProps {
  children: ReactNode;
  tone?: StatusChipTone;
}

const TONE_CLASSES: Record<StatusChipTone, string> = {
  // Muted dark emerald with cream text and a brass edge — the platform's own
  // "this is yours, and it is live" treatment.
  active: 'border-brand/70 bg-frame text-textOnDark',
  info: SOFT_TONE_CLASS.info,
  warning: SOFT_TONE_CLASS.warning,
  neutral: 'border-border bg-surfaceAlt text-textSecondary',
};

/**
 * A compact status badge (docs/UI_GUIDELINES.md §8 "Badges") — a *state*
 * indicator, never a control: no hover affordance, no pointer cursor, and no
 * click target. Used by the game catalog/dashboard cards for "Active" (the
 * viewer has a session in progress) and for a game's own non-default
 * operational status.
 *
 * The state is always in the text; the tone only reinforces it, so nothing is
 * communicated by colour alone (docs/UI_GUIDELINES.md §2, §9). Uppercasing is
 * a CSS transform so the accessible name stays ordinary prose.
 */
export function StatusChip({ children, tone = 'neutral' }: StatusChipProps) {
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 rounded-md border px-2 py-0.5 text-[0.6875rem] font-bold uppercase leading-tight tracking-[0.1em] ${TONE_CLASSES[tone]}`}
    >
      {children}
    </span>
  );
}
